import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { WEEKDAYS, freqText, nz } from "@/pages/admin/suppliers/common";

const pad = (n) => String(n ?? 0).padStart(2, "0");
export const scraperSched = (s) => ({ frequency: s.schedule_frequency || "daily", time: `${pad(s.schedule_hour ?? 19)}:${pad(s.schedule_minute ?? 45)}`,
  weekday: s.schedule_weekday ?? 6, interval_hours: s.schedule_interval_hours ?? 6 });

export function ScheduleSummary({ s }) {
  const on = s.enabled && s.schedule_enabled !== false;
  return (
    <span data-testid={`scraper-schedule-summary-${s.id}`}>
      <Clock size={11} className="inline -mt-0.5" /> {s.schedule_enabled === false ? "Schedule off (manual only)" : `${freqText(scraperSched(s))} NZ`}
      {on && s.next_run_at && <> · next {nz(s.next_run_at)}</>}
    </span>
  );
}

export function ScraperScheduleEditor({ s, onSaved, onCancel }) {
  const [f, setF] = useState({ schedule_enabled: s.schedule_enabled !== false, ...scraperSched(s) });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    const [h, m] = (f.time || "19:45").split(":").map(Number);
    setBusy(true);
    try {
      await api.put(`/admin/scraper-sources/${s.id}`, { schedule_enabled: f.schedule_enabled, schedule_frequency: f.frequency,
        schedule_hour: h, schedule_minute: m, schedule_weekday: Number(f.weekday), schedule_interval_hours: Number(f.interval_hours) });
      toast.success("Schedule saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save schedule"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 pt-4 border-t space-y-3" data-testid={`scraper-schedule-editor-${s.id}`}>
      <p className="font-semibold text-sm flex items-center gap-2"><CalendarClock size={15} className="text-accent" /> Time schedule (Pacific/Auckland)</p>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.schedule_enabled} onCheckedChange={(v) => set("schedule_enabled", v)} data-testid="scraper-sched-enabled" />Run automatically on this schedule</label>
      <div className="grid sm:grid-cols-4 gap-3">
        <div><Label className="text-xs">Frequency</Label>
          <Select value={f.frequency} onValueChange={(v) => set("frequency", v)}>
            <SelectTrigger data-testid="scraper-sched-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>{[["hourly", "Hourly"], ["daily", "Daily"], ["weekly", "Weekly"], ["custom", "Every N hours"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">{f.frequency === "hourly" ? "Minute past the hour (uses :MM)" : "Start time"}</Label>
          <Input type="time" value={f.time} onChange={(e) => set("time", e.target.value)} data-testid="scraper-sched-time" /></div>
        {f.frequency === "weekly" && <div><Label className="text-xs">Day</Label>
          <Select value={String(f.weekday)} onValueChange={(v) => set("weekday", Number(v))}>
            <SelectTrigger data-testid="scraper-sched-weekday"><SelectValue /></SelectTrigger>
            <SelectContent>{WEEKDAYS.map((w, i) => <SelectItem key={w} value={String(i)}>{w}</SelectItem>)}</SelectContent>
          </Select></div>}
        {f.frequency === "custom" && <div><Label className="text-xs">Every N hours</Label>
          <Input type="number" min={1} max={168} value={f.interval_hours} onChange={(e) => set("interval_hours", e.target.value)} data-testid="scraper-sched-interval" /></div>}
      </div>
      <p className="text-[11px] text-muted-foreground">Checked every 15 minutes. If another supplier job is running at that time, this scraper waits its turn — scrapers never overlap.</p>
      <div className="flex gap-2">
        <Button onClick={save} disabled={busy} className="bg-steel text-white" data-testid="scraper-sched-save">Save schedule</Button>
        <Button variant="ghost" onClick={onCancel} data-testid="scraper-sched-cancel">Cancel</Button>
      </div>
    </div>
  );
}
